import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import Highlights from "./components/Highlights";
import MainInfo from "./components/MainInfo";
import Notes from "./components/Notes";
import ProductionScriptBlock from "./components/ProductionScriptBlock";
import { PageHeading, TeamHeading } from "@/components/Layout/Parts";
import SceneNotesTable from "./components/SceneNotesTable";
import PropsTable from "./components/PropsTable";
import ShoppingListTable from "./components/ShoppingListTable";
import ActorsTable from "./components/ActorsTable";
import LocationsTable from "./components/LocationsTable";
import {
  ProductionProjectResource,
  ProductionSceneActorResource,
  ProductionSceneLocationResource,
  ProductionSceneNoteResource,
  ProductionScenePropResource,
  ProductionSceneShoppingItemResource,
  ProductionSceneVoiceoverResource,
} from "@/types/resources";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import ContentSpinner from "@/components/ContentSpinner";
import VoiceoversTable from "./components/VoiceoversTable";
import BackButton from "@/components/BackButton";

const PreProductionProject = () => {
  const { projectId, teamId } = useParams();

  const navigate = useNavigate();

  const alerts = useAlerts();

  const [activeScriptId, setActiveScriptId] = useState<number | null>(null);

  const { isLoading, data: project } = useQuery(
    ["production_projects", { projectId }],
    () =>
      api.get<never, ProductionProjectResource>(
        `/v1/production/projects/${projectId}`
      ),
    {
      staleTime: 60 * 1000,
      retry: 2,
      onError: (err) => {
        if ((err as any).response?.status === 404) {
          return navigate("/404", {
            replace: true,
          });
        }
        alerts.error();
      },
    }
  );

  useEffect(() => {
    if (project?.scripts?.find((script) => script.id === activeScriptId)) {
      return;
    }

    setActiveScriptId(project?.scripts?.[0]?.id ?? null);
  }, [project?.id, activeScriptId, project?.scripts]);

  const activeScript = useMemo(() => {
    return project?.scripts?.find((s) => s.id === activeScriptId);
  }, [project, activeScriptId]);

  const data = useMemo(() => {
    if (!activeScript) {
      return null;
    }

    return activeScript.scenes?.reduce<{
      shoppingItems: ProductionSceneShoppingItemResource[][];
      props: ProductionScenePropResource[][];
      locations: ProductionSceneLocationResource[][];
      actors: ProductionSceneActorResource[][];
      notes: ProductionSceneNoteResource[][];
      voiceovers: ProductionSceneVoiceoverResource[][];
      scenes: number[];
    }>(
      (acc, curr) => {
        acc.scenes.push(curr.id);
        acc.locations.push(curr.locations!);
        acc.props.push(curr.props!);
        acc.actors.push(curr.actors!);
        acc.shoppingItems.push(curr.shoppingItems!);
        acc.notes.push(curr.notes!);
        acc.voiceovers.push(curr.voiceovers!);
        return acc;
      },
      {
        props: [],
        locations: [],
        shoppingItems: [],
        scenes: [],
        actors: [],
        notes: [],
        voiceovers: [],
      }
    );
  }, [activeScript]);

  if (isLoading || !project) {
    return <ContentSpinner />;
  }

  return (
    <>
      <TeamHeading />
      <PageHeading>{project.name}</PageHeading>
      <BackButton link={`/teams/${teamId}/pre-production`} />
      <MainInfo project={project} />
      <Notes notes={project.notes} />
      <ProductionScriptBlock
        setActiveScriptId={setActiveScriptId}
        activeScriptId={activeScriptId}
        project={project}
        sidebarData={data}
      />
      {!!data && (
        <>
          {!!data?.scenes.length && (
            <>
              <Highlights activeScript={activeScript!} />
              <LocationsTable scenes={data.scenes} locations={data.locations} />
              <PropsTable scenes={data.scenes} props={data.props} />
              <ShoppingListTable
                scenes={data.scenes}
                shoppingItems={data.shoppingItems}
              />
              <ActorsTable scenes={data.scenes} actors={data.actors} />
              <VoiceoversTable
                scenes={data.scenes}
                voiceovers={data.voiceovers}
              />
              <SceneNotesTable scenes={data.scenes} notes={data.notes} />
            </>
          )}
        </>
      )}
    </>
  );
};

export default PreProductionProject;
